import { cn } from '@/lib/utils'

/**
 * The one rendering of a Scoreboar score: chat options, queue rows, the post
 * editor, the landing demo, analytics and the account read all show it
 * through this badge. It is a light key like the rest of the app, in ink
 * only: five signal bars fill to the level and the number says it. (It was a
 * traffic-light chip with a flame, the old look.)
 *
 * The tooltip says what the number means; with Scoreboar v8 that is the
 * share of ordinary posts it is expected to beat from accounts like the
 * author's: size, age and activity, not follower count alone.
 */

export function viralityLabel(score: number): string {
  if (score >= 4.5) return 'Very High'
  if (score >= 3.5) return 'High'
  if (score >= 2.5) return 'Medium'
  if (score >= 1.5) return 'Low'
  return 'Very Low'
}

/** Five bars, rising like a signal meter; the first `level` are ink. */
const BAR_HEIGHTS = [4, 6, 8, 10, 12]

function SignalBars({ level, className }: { level: number; className?: string }) {
  const filled = Math.round(level)
  return (
    <span className={cn('flex h-3 items-end gap-[2px]', className)} aria-hidden="true">
      {BAR_HEIGHTS.map((height, index) => (
        <span
          key={height}
          className={cn('w-[3px] rounded-[1px]', index < filled ? 'bg-foreground' : 'bg-foreground/15')}
          style={{ height }}
        />
      ))}
    </span>
  )
}

interface ViralityMeterProps {
  /** Scoreboar score on the 1-5 scale. */
  score: number
  /** Put the band word ("High") after the number. */
  showLabel?: boolean
  /** Put "Scoreboar" before the bars, where the badge sits away from other scores. */
  named?: boolean
  /** 0 to 100 from Scoreboar v8: the share of ordinary posts from accounts like the author's it is expected to beat. */
  percentile?: number | null
  /** A new score is on its way (the media changed): the old one shows dimmed until it lands. */
  pending?: boolean
  className?: string
}

export function ViralityMeter({ score, showLabel = false, named = false, percentile, pending = false, className }: ViralityMeterProps) {
  const clamped = Math.min(5, Math.max(0, score))
  const shown = clamped % 1 === 0 ? String(clamped) : clamped.toFixed(1)
  const read = typeof percentile === 'number'
    ? `Scoreboar ${shown}/5: expected to beat ${Math.round(percentile)}% of ordinary posts from accounts like yours (${viralityLabel(clamped)})`
    : `Scoreboar score ${clamped.toFixed(1)}/5. Predicted engagement for this text (${viralityLabel(clamped)})`

  return (
    <span
      title={pending ? 'Rescoring with what is attached' : read}
      aria-label={pending ? 'Rescoring with what is attached' : read}
      aria-busy={pending || undefined}
      role="img"
      className={cn(
        'key key-lite inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full pl-2 pr-2.5 text-[11px] leading-none text-muted-foreground transition-opacity',
        pending && 'opacity-50',
        className,
      )}
    >
      {named && <span>Scoreboar</span>}
      <SignalBars level={clamped} />
      <span className="tabular-nums">
        <span className="font-semibold text-foreground">{shown}</span>/5
      </span>
      {showLabel && <span>{viralityLabel(clamped).toLowerCase()}</span>}
    </span>
  )
}
